/* eslint-disable @typescript-eslint/no-unnecessary-condition */
import {
  formatSdgAlignmentAsPercentage,
  sdgShowThreshold,
} from '@upright/common/model/output/scores/sdg-alignment/shared';
import {
  SdgAlignmentStrength,
  SdgAlignmentValence,
  SdgUnitsMode,
} from '@upright/common/model/output/scores/sdg-alignment/types';
import { getAlignmentsOfValenceWithAdjustedValues } from '@upright/common/model/output/scores/sdg-alignment/utils';
import { getTextStyle } from '@upright/ui/common/generic/content/texts/bou-text/text-style';
import { BouColor } from '@upright/ui/common/generic/global/bou-colors';
import { tabletOrWider } from '@upright/ui/common/generic/layout/breakpoint-utils';
import { ColorBar } from '@upright/ui/common/upright/model/output/scores/sdg-alignments/sdg-row/sdg-alignment-bars/sdg-alignment-bar/color-bar';
import { color } from 'd3-color';
import Skeleton from 'react-loading-skeleton';
import styled, { css } from 'styled-components';
import { SdgRowProps } from '../..';
import { useSdgAlignmentsContext } from '../../../context';
import { getSdgColor } from '../../../shared';

export interface SdgBarProps extends SdgRowProps {
  valence: SdgAlignmentValence;
}

export const SdgAlignmentBar = (props: SdgBarProps) => {
  const context = useSdgAlignmentsContext();

  const { sdgId, valence } = props;

  const alignments = sdgId ? context.sdgAlignmentsRequest.data?.[sdgId] : undefined;
  const unitsMode = context.viewOptions.unitsMode;

  const { strong, normal, weak } = getAlignmentsOfValenceWithAdjustedValues(alignments, props.valence, unitsMode);
  const [strongAlignmentValue, normalAlignmentValue, weakAlignmentValue] = [
    strong.adjustedValue,
    normal.adjustedValue,
    weak.adjustedValue,
  ];

  if (context.sdgAlignmentsRequest.isLoading && !context.sdgAlignmentsRequest.data) {
    return (
      <Root {...{ valence }}>
        <div style={{ paddingLeft: '2em', paddingRight: '2em' }}>
          <Skeleton />
        </div>
      </Root>
    );
  }

  if (!normal && !strong && !weak) {
    return <Root valence={valence} />;
  }

  const combinedAlignment = (() => {
    if (strongAlignmentValue || normalAlignmentValue || weakAlignmentValue) {
      return (strongAlignmentValue || 0) + (normalAlignmentValue || 0) + (weakAlignmentValue || 0);
    }
    return weakAlignmentValue ?? strongAlignmentValue ?? normalAlignmentValue;
  })();

  const shouldShow = !!combinedAlignment && combinedAlignment >= sdgShowThreshold;

  const combinedText = formatSdgAlignmentAsPercentage(combinedAlignment, 1, true);
  const maxAlignment = unitsMode === SdgUnitsMode.SUMMARY_PERCENTAGE ? context.maxAlignment : 1.0;

  const disableInteraction = !!context.viewOptions.disableInteraction;

  const pureStrongBar = strong && (
    <ColorBar
      valence={valence}
      value={strongAlignmentValue}
      alignment={getStrenthBarWidth(strongAlignmentValue, combinedAlignment)}
      strength={SdgAlignmentStrength.STRONG}
    />
  );
  const pureModerateBar = normal && (
    <ColorBar
      valence={valence}
      value={normalAlignmentValue}
      alignment={getStrenthBarWidth(normalAlignmentValue, combinedAlignment)}
      strength={SdgAlignmentStrength.NORMAL}
    />
  );
  const pureWeakBar = weak && (
    <ColorBar
      valence={valence}
      value={weakAlignmentValue}
      alignment={getStrenthBarWidth(weakAlignmentValue, combinedAlignment)}
      strength={SdgAlignmentStrength.WEAK}
    />
  );

  const pureBars =
    valence === SdgAlignmentValence.ALIGNED ? (
      <>
        {pureStrongBar}
        {pureModerateBar}
        {pureWeakBar}
      </>
    ) : (
      <>
        {pureWeakBar}
        {pureModerateBar}
        {pureStrongBar}
      </>
    );

  return (
    <Root valence={valence}>
      {shouldShow && (
        <FlexWrapper
          disableInteraction={disableInteraction}
          onClick={() => {
            if (disableInteraction) {
              return;
            }
            props.onClick(props.valence, props.sdgId);
          }}
          valence={valence}
        >
          <BarWrap alignment={combinedAlignment} maxAlignment={maxAlignment}>
            {unitsMode === SdgUnitsMode.SUMMARY_PERCENTAGE && (
              <>
                <ColorBar
                  valence={valence}
                  alignment={getStrenthBarWidth(combinedAlignment, combinedAlignment)}
                  strength={SdgAlignmentStrength.STRONG}
                />
                <ValueText valence={valence}>
                  <span>{combinedText}</span>
                </ValueText>
              </>
            )}
            {unitsMode === SdgUnitsMode.PURE_REVENUE_SHARES && pureBars}
          </BarWrap>
        </FlexWrapper>
      )}
    </Root>
  );
};

const Root = styled.div<{ valence: SdgAlignmentValence }>`
  width: 50%;
  display: flex;
  justify-content: ${props => (props.valence === SdgAlignmentValence.ALIGNED ? 'flex-start' : 'flex-end')};
`;

// HACK: The value text should not be part of any width calculations, so we make the whole container
// narrower by the width of the text. Only after that is the container base width used for relative
// weights of the bars. We then append the value text to the end of the bar with "position: absolute".
// const valueTextWidth = '4.5em';
const valueTextWidth = '0em';

const FlexWrapper = styled.div<{ valence: SdgAlignmentValence; disableInteraction: boolean }>`
  ${props =>
    tabletOrWider(css`
      &:hover {
        ${BarWrap} {
          border-color: ${!props.disableInteraction && BouColor.TOOL_BACKGROUND_MUTED_BLACK};
        }
      }

      cursor: ${!props.disableInteraction && 'pointer'};
    `)};
  width: calc(100% - ${valueTextWidth});
  display: flex;
  align-items: center;
  ${props =>
    props.valence === SdgAlignmentValence.MISALIGNED &&
    css`
      flex-direction: row-reverse;
    `}
  position: relative;
`;

const BarWrap = styled.div<{ alignment: number; maxAlignment: number }>`
  ${getTextStyle({ size: 'xxs' })}
  height: 1em;
  width: ${props => getTotalBarWidth(props.alignment, props.maxAlignment)}%;
  border-radius: 2px;
  border: 1px solid transparent;
  transition: border-color 0.2s ease-in-out;
  display: flex;
  align-items: center;
  overflow: visible;
  position: relative;
`;

const ValueText = styled.div<{ valence: SdgAlignmentValence }>`
  width: ${valueTextWidth};
  white-space: nowrap;
  display: flex;
  justify-content: ${props => (props.valence === SdgAlignmentValence.ALIGNED ? 'flex-start' : 'flex-end')};
  flex-grow: 0;
  flex-shrink: 0;
  position: absolute;
  ${props => (props.valence === SdgAlignmentValence.ALIGNED ? 'left: 100%' : 'right: 100%')};
  ${props => {
    if (props.valence === SdgAlignmentValence.ALIGNED) {
      return 'padding-left: 0.4em';
    }
    if (props.valence === SdgAlignmentValence.MISALIGNED) {
      return 'padding-right: 0.4em';
    }
    return;
  }};
  color: ${props => color(getSdgColor(props.valence, SdgAlignmentStrength.STRONG))?.darker(0.6).hex()};
  font-weight: bold;
`;

function getTotalBarWidth(value: number, maxValue: number) {
  const baseValue = (() => {
    if (maxValue < 0.15) {
      return value * 5;
    }
    if (maxValue < 0.25) {
      return value * 3;
    }
    if (maxValue < 0.5) {
      return value * 1.5;
    }
    return value;
  })();
  return baseValue * 100;
}

function getStrenthBarWidth(alignment: number, totalAlignment: number) {
  return (alignment / totalAlignment) * 100;
}
